import React, { useState, useEffect } from 'react'
import ReactApexChart from 'react-apexcharts'
import { attendanceService, expenseService, projectService, quotationService, purchaseOrderService, invoiceService, salaryService, creditNoteService } from '../services/api'
import { formatCurrency } from '../utils/currency'
import { useTheme } from '../context/ThemeContext'
import CollapsibleFilters from '../components/CollapsibleFilters'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import { financialDashboardService } from '../services/financialDashboard'
import { formatCurrency as formatFinancialCurrency } from '../utils/currency'
import { exportFinancialPDF, exportFinancialExcel } from '../utils/financialExportUtils'
import FinancialStatements from '../components/FinancialStatements'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'

const STATUS_COLORS = {
  Proposal: '#9333ea',
  Confirm: '#2563eb',
  Close: '#16a34a',
  Paid: '#16a34a',
  Unpaid: '#dc2626',
  Ongoing: '#f59e0b',
  Pending: '#f59e0b',
  Approve: '#16a34a',
  Reject: '#dc2626',
  Revenue: '#16a34a',
  Expenses: '#dc2626',
  Overhead: '#f59e0b',
  Rental: '#8b5cf6',
  Material: '#0ea5e9',
  Subcon: '#f97316',
  Petrol: '#eab308',
  Others: '#64748b',
  Salary: '#dc2626',
  // New expense categories matching Financial Report 2025
  'Account Fees': '#14b8a6',
  'Insurance & Roadtax': '#f43f5e',
  'Office Supplies': '#22c55e',
  'Professional Fees': '#3b82f6',
  'Admin Fees': '#8b5cf6',
  'Project Management Fees': '#f59e0b',
  'Travelling & Accommodation': '#ec4899',
  'Repair & Maintenance': '#6366f1',
  'Site Expenses': '#84cc16',
  'Project Commission': '#f97316',
  'Staff Welfare': '#06b6d4',
  'Wages & Salaries': '#ef4444',
  'Staff Bonus': '#eab308',
  'Materials & Supplies': '#0ea5e9',
  'Subcontractor Costs': '#f97316',
  'Transportation & Rental Services': '#8b5cf6',
  'Site Insurance': '#f43f5e',
  default: '#6b7280'
}

const getChartColor = (label) => STATUS_COLORS[label] || STATUS_COLORS.default

// Expense categories tracked in dashboard breakdowns (key = lowercase expense_category).
// Updated to match Financial Report 2025 naming conventions
const EXPENSE_CATEGORY_KEYS = {
  overhead: 'overhead',
  rental: 'rental',
  material: 'material',
  subcon: 'subcon',
  petrol: 'petrol',
  others: 'others',
  // New categories matching Financial Report 2025
  'account fees': 'account fees',
  'insurance & roadtax': 'insurance & roadtax',
  'office supplies': 'office supplies',
  'professional fees': 'professional fees',
  'admin fees': 'admin fees',
  'project management fees': 'project management fees',
  'travelling & accommodation': 'travelling & accommodation',
  'repair & maintenance': 'repair & maintenance',
  'site expenses': 'site expenses',
  'project commission': 'project commission',
  'staff welfare': 'staff welfare',
  'wages & salaries': 'wages & salaries',
  'staff bonus': 'staff bonus',
  'materials & supplies': 'materials & supplies',
  'subcontractor costs': 'subcontractor costs',
  'transportation & rental services': 'transportation & rental services',
  'site insurance': 'site insurance'
}

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
]


const PERIOD_TYPE_OPTIONS = [
  { value: 'month', label: 'Monthly (MMM\'YY)' },
  { value: 'quarter', label: 'Quarterly (Q#\'YY)' },
  { value: 'annual', label: 'Yearly (YYYY)' }
]

function FlowbitePieChart({ title, data, isDark }) {
  const series = data.map((d) => Number(d.value || 0))
  const labels = data.map((d) => d.name)
  const colors = labels.map((label) => getChartColor(label))
  const axisColor = isDark ? '#dbeafe' : '#1f2937'
  const gridColor = isDark ? '#334155' : '#e5e7eb'

  const options = {
    chart: {
      type: 'donut',
      fontFamily: 'inherit',
      toolbar: { show: false },
      foreColor: axisColor
    },
    labels,
    colors,
    stroke: {
      colors: [isDark ? '#0f172a' : '#ffffff'],
      width: 2
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const value = series[opts.seriesIndex] || 0
        return `${labels[opts.seriesIndex]}: ${value}`
      }
    },
    legend: {
      position: 'bottom',
      fontSize: '13px',
      labels: { colors: axisColor }
    },
    tooltip: {
      y: {
        formatter: (value) => `${value}`
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '52%'
        }
      }
    },
    grid: {
      borderColor: gridColor
    }
  }

  return (
    <div className={`rounded-lg p-6 shadow ${isDark ? 'bg-slate-900 ring-1 ring-slate-700' : 'bg-white'}`}>
      <h3 className={`mb-4 text-center text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>{title}</h3>
      {series.length > 0 ? (
        <ReactApexChart options={options} series={series} type="donut" height={280} />
      ) : (
        <div className={`flex h-[280px] items-center justify-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>No records</div>
      )}
    </div>
  )
}

function FlowbiteBarChart({ title, categories, series, stacked = true, height = 340, isDark }) {
  const axisColor = isDark ? '#dbeafe' : '#1f2937'
  const gridColor = isDark ? '#334155' : '#e5e7eb'

  const options = {
    chart: {
      type: 'bar',
      stacked,
      fontFamily: 'inherit',
      toolbar: { show: false },
      foreColor: axisColor
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 4
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 1,
      colors: ['transparent']
    },
    xaxis: {
      categories,
      labels: {
        style: {
          colors: categories.map(() => axisColor)
        }
      }
    },
    yaxis: {
      labels: {
        formatter: (val) => Number(val || 0).toLocaleString(),
        style: {
          colors: [axisColor]
        }
      }
    },
    grid: {
      strokeDashArray: 4,
      borderColor: gridColor
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      labels: { colors: axisColor }
    },
    tooltip: {
      y: {
        formatter: (val) => Number(val || 0).toLocaleString()
      }
    },
    colors: series.map((s) => getChartColor(s.name))
  }

  return (
    <div className={`rounded-lg p-6 shadow ${isDark ? 'bg-slate-900 ring-1 ring-slate-700' : 'bg-white'}`}>
      <h3 className={`mb-4 text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>{title}</h3>
      <ReactApexChart options={options} series={series} type="bar" height={height} />
    </div>
  )
}

function ProfitLossBarChart({ title, categories, data, height = 340, isDark }) {
  const axisColor = isDark ? '#dbeafe' : '#1f2937'
  const gridColor = isDark ? '#334155' : '#e5e7eb'

  const options = {
    chart: {
      type: 'bar',
      fontFamily: 'inherit',
      toolbar: { show: false },
      foreColor: axisColor
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 4,
        colors: {
          ranges: [
            { from: -Number.MAX_SAFE_INTEGER, to: -0.000001, color: '#dc2626' },
            { from: 0, to: Number.MAX_SAFE_INTEGER, color: '#16a34a' }
          ]
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      categories,
      labels: {
        style: {
          colors: categories.map(() => axisColor)
        }
      }
    },
    yaxis: {
      labels: {
        formatter: (val) => Number(val || 0).toLocaleString(),
        style: {
          colors: [axisColor]
        }
      }
    },
    grid: {
      strokeDashArray: 4,
      borderColor: gridColor
    },
    legend: {
      show: false
    },
    tooltip: {
      y: {
        formatter: (val) => Number(val || 0).toLocaleString()
      }
    }
  }

  const series = [
    {
      name: 'Profit/Loss',
      data
    }
  ]

  return (
    <div className={`rounded-lg p-6 shadow ${isDark ? 'bg-slate-900 ring-1 ring-slate-700' : 'bg-white'}`}>
      <h3 className={`mb-4 text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>{title}</h3>
      <ReactApexChart options={options} series={series} type="bar" height={height} />
    </div>
  )
}

// Financial Dashboard Components
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const FinancialReconciliationStatus = ({ reconciliation_status, isDark, onBulkPost, onAutoFix }) => {
  const reconciliation = reconciliation_status || {}

  return (
    <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
      <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>📊 Reconciliation Status</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-3 rounded-lg text-center ${reconciliation?.claim?.double_entry_accounting?.balanced ? 'bg-green-100' : 'bg-red-100'} ${isDark ? (reconciliation?.claim?.double_entry_accounting?.balanced ? 'bg-green-900' : 'bg-red-900') : ''}`}>
          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Double-Entry</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {reconciliation?.claim?.double_entry_accounting?.balanced ? '✓ Balanced' : '⚠ Imbalanced'}
          </p>
        </div>

        <div className={`p-3 rounded-lg text-center ${reconciliation?.claim?.outgoing_invoices?.reconciled ? 'bg-green-100' : 'bg-yellow-100'} ${isDark ? (reconciliation?.claim?.outgoing_invoices?.reconciled ? 'bg-green-900' : 'bg-yellow-900') : ''}`}>
          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Invoices</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {reconciliation?.claim?.outgoing_invoices?.reconciled ? '✓ Reconciled' : '⚠ Mismatch'}
          </p>
        </div>

        <div className={`p-3 rounded-lg text-center ${reconciliation?.claim?.expenses?.reconciled ? 'bg-green-100' : 'bg-yellow-100'} ${isDark ? (reconciliation?.claim?.expenses?.reconciled ? 'bg-green-900' : 'bg-yellow-900') : ''}`}>
          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Expenses</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {reconciliation?.claim?.expenses?.reconciled ? '✓ Reconciled' : '⚠ Mismatch'}
          </p>
        </div>

        <div className={`p-3 rounded-lg text-center ${reconciliation?.claim?.unposted_documents?.total > 0 ? 'bg-yellow-100' : 'bg-green-100'} ${isDark ? (reconciliation?.claim?.unposted_documents?.total > 0 ? 'bg-yellow-900' : 'bg-green-900') : ''}`}>
          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Unposted</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {reconciliation?.claim?.unposted_documents?.total || 0}
          </p>
        </div>
      </div>

      {(reconciliation?.discrepancies?.length > 0 || reconciliation?.claim?.unposted_documents?.total > 0) && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={onBulkPost}
            className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition"
          >
            📝 Post Missing Entries
          </button>
          <button
            onClick={onAutoFix}
            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition"
          >
            🔧 Auto-Fix Issues
          </button>
        </div>
      )}
    </div>
  )
}

const FinancialKPICards = ({ balance_sheet, income_statement, cash_flow, isDark, formatCurrency }) => {
  const bs = balance_sheet || {}
  const is = income_statement || {}
  const cf = cash_flow || {}

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Revenue</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {formatCurrency(is?.revenue?.net_revenue || 0)}
            </p>
          </div>
          <div className="text-3xl">💰</div>
        </div>
        <p className={`text-xs mt-2 ${is?.revenue?.net_revenue > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {is?.profit_margins?.net_profit_margin || '0%'} net margin
        </p>
      </div>

      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Net Profit</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {formatCurrency(is?.net_profit || 0)}
            </p>
          </div>
          <div className="text-3xl">📈</div>
        </div>
        <p className={`text-xs mt-2 ${is?.net_profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {is?.net_profit > 0 ? 'Positive' : 'Negative'} profitability
        </p>
      </div>

      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Assets</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {formatCurrency(bs?.assets?.total_assets || 0)}
            </p>
          </div>
          <div className="text-3xl">🏦</div>
        </div>
        <p className={`text-xs mt-2 ${bs?.balance_verification?.balanced ? 'text-green-600' : 'text-red-600'}`}>
          {bs?.balance_verification?.balanced ? '✓ Balanced' : '⚠ Imbalanced'}
        </p>
      </div>

      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Cash Position</p>
            <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {formatCurrency(cf?.cash_position?.ending_cash || 0)}
            </p>
          </div>
          <div className="text-3xl">💵</div>
        </div>
        <p className={`text-xs mt-2 ${cf?.net_cash_flow > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {cf?.net_cash_flow > 0 ? 'Positive' : 'Negative'} cash flow
        </p>
      </div>
    </div>
  )
}

const FinancialRevenueChart = ({ income_statement, isDark, formatCurrency }) => {
  const income = income_statement || {}

  const revenueData = [
    {
      name: 'Revenue',
      sales: income?.revenue?.sales_revenue || 0,
      service: income?.revenue?.service_revenue || 0,
      total: income?.revenue?.net_revenue || 0
    }
  ]

  return (
    <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Revenue Breakdown</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={revenueData}>
          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
          <XAxis dataKey="name" stroke={isDark ? '#94a3b8' : '#4b5563'} />
          <YAxis stroke={isDark ? '#94a3b8' : '#4b5563'} />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
              color: isDark ? '#ffffff' : '#000000'
            }}
            formatter={(value) => formatCurrency(value)}
          />
          <Legend />
          <Bar dataKey="sales" name="Sales Revenue" fill="#3B82F6" />
          <Bar dataKey="service" name="Service Revenue" fill="#10B981" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const FinancialExpenseChart = ({ income_statement, isDark, formatCurrency }) => {
  const income = income_statement || {}

  const expenseData = [
    { name: 'Cost of Services Rendered', value: Math.abs(income?.cost_of_services_rendered?.total_cogs || 0) },
    { name: 'Operating Expenses', value: Math.abs(income?.operating_expenses?.total_operating_expenses || 0) },
    { name: 'Other Expenses', value: Math.abs(income?.other_income_expenses?.net_other || 0) }
  ].filter(item => item.value > 0)

  return (
    <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Expense Breakdown</h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={expenseData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {expenseData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
              color: isDark ? '#ffffff' : '#000000'
            }}
            formatter={(value) => formatCurrency(value)}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

const FinancialRatiosPanel = ({ financial_ratios, isDark }) => {
  const ratios = financial_ratios || {}

  const formatRatio = (value) => {
    // Handle string values that already contain formatting
    if (typeof value === 'string') {
      if (value.includes('%')) return value
      // Try to parse string to number
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) return numValue.toFixed(2)
      return value
    }
    // Handle number values
    if (typeof value === 'number' && !isNaN(value)) {
      return value.toFixed(2)
    }
    return '0.00'
  }

  const getSafeRatio = (ratioPath) => {
    try {
      const value = ratioPath
      return value !== undefined && value !== null ? value : '0.00'
    } catch {
      return '0.00'
    }
  }

  return (
    <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800' : 'bg-white'} shadow`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Financial Ratios</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
          <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Current Ratio</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {formatRatio(getSafeRatio(ratios?.liquidity_ratios?.current_ratio))}
          </p>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
          <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Debt-to-Equity</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {formatRatio(getSafeRatio(ratios?.solvency_ratios?.debt_to_equity))}
          </p>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
          <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Net Profit Margin</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {getSafeRatio(ratios?.profitability_ratios?.net_profit_margin) || '0%'}
          </p>
        </div>
        <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
          <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Return on Assets</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
            {getSafeRatio(ratios?.profitability_ratios?.return_on_assets) || '0%'}
          </p>
        </div>
      </div>
    </div>
  )
}

const FinancialFilters = ({ startDate, endDate, setStartDate, setEndDate, onPDFExport, onExcelExport, isDark, loading }) => (
  <div className={`rounded-lg p-4 shadow ${isDark ? 'bg-slate-900 ring-1 ring-slate-700' : 'bg-white'}`}>
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          Start Date
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100' : 'border-gray-300 text-gray-700'}`}
        />
      </div>
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
          End Date
        </label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100' : 'border-gray-300 text-gray-700'}`}
        />
      </div>
      <div className="flex items-end gap-2">
        <button
          onClick={onPDFExport}
          disabled={loading}
          className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition"
        >
          📄 Export PDF
        </button>
        <button
          onClick={onExcelExport}
          disabled={loading}
          className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition"
        >
          📊 Export Excel
        </button>
      </div>
      <div className="flex items-end">
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
          Generate professional financial reports for stakeholder review and compliance.
        </p>
      </div>
    </div>
  </div>
)

export default function Dashboard() {
  const { isDark } = useTheme()
  const [projects, setProjects] = useState([])
  const [creditNotes, setCreditNotes] = useState([])
  const [expenses, setExpenses] = useState([])
  const [attendances, setAttendances] = useState([])
  const [quotations, setQuotations] = useState([])
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [invoices, setInvoices] = useState([])
  const [salaryRecords, setSalaryRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeView, setActiveView] = useState('operations')

  // Financial date range state (needed for useEffect dependency)
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0])
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])


  const [filters, setFilters] = useState({
    operations: {
      years: [],
      // No default month restriction — show all months (including older ones
      // like backdated expense_date entries) until the user picks a filter.
      months: [],
      quarters: [],
      periodType: 'month',
      viewMode: 'period', // 'period' or 'project' or 'claim'
      projectIds: [],
      labelType: 'po_no'
    }
  })

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (activeView === 'financial') {
      fetchFinancialData()
    }
  }, [startDate, endDate])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [projectsRes, expensesRes, attendancesRes, quotationsRes, posRes, invoicesRes, salaryRes, creditNotesRes] = await Promise.all([
        projectService.getAll(),
        expenseService.getAll(),
        attendanceService.getAll(),
        quotationService.getAll().catch(() => ({ data: { data: [] } })),
        purchaseOrderService.getAll().catch(() => ({ data: { data: [] } })),
        invoiceService.getAll().catch(() => ({ data: { data: [] } })),
        salaryService.getSummary().catch(() => salaryService.getAll()).catch(() => ({ data: { data: [] } })),
        creditNoteService.getAll().catch(() => ({ data: { data: [] } }))
      ])

      const projectsList = projectsRes.data.data || []
      const expensesList = expensesRes?.data?.data || []
      const attendanceList = attendancesRes?.data?.data || []
      const quotationsList = quotationsRes?.data?.data || []
      const poList = posRes?.data?.data || []
      const invoicesList = invoicesRes?.data?.data || []
      const salaryList = salaryRes?.data?.data || []
      const creditNotesList = creditNotesRes?.data?.data || []

      setProjects(projectsList)
      setExpenses(expensesList)
      setAttendances(attendanceList)
      setQuotations(quotationsList)
      setPurchaseOrders(poList)
      setInvoices(invoicesList)
      setSalaryRecords(salaryList)
      setCreditNotes(creditNotesList)
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      setError(error.response?.data?.message || error.message || 'Failed to fetch dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const toMonthKey = (value) => {
    if (!value) return null
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  const toYear = (value) => {
    const monthKey = toMonthKey(value)
    return monthKey ? monthKey.split('-')[0] : null
  }

  const toQuarter = (value) => {
    if (!value) return null
    const monthKey = toMonthKey(value)
    if (!monthKey) return null
    const [year, month] = monthKey.split('-').map(Number)
    const quarter = Math.ceil(month / 3)
    const yearShort = year.toString().slice(-2)
    return `Q${quarter}'${yearShort}`
  }

  const toAnnual = (value) => {
    const year = toYear(value)
    return year ? year : null
  }

  const toMonthShort = (value) => {
    if (!value) return null
    const monthKey = toMonthKey(value)
    if (!monthKey) return null
    const [year, month] = monthKey.split('-').map(Number)
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const yearShort = year.toString().slice(-2)
    return `${monthNames[month - 1]}'${yearShort}`
  }

  const getPeriodKey = (value, periodType) => {
    if (periodType === 'quarter') return toQuarter(value)
    if (periodType === 'annual') return toAnnual(value)
    return toMonthShort(value)
  }

  const toProjectId = (record) => {
    const id = Number(record?.project_id || record?.Project?.project_id || record?.projectId)
    return Number.isFinite(id) && id > 0 ? id : null
  }

  const matchesDateFilter = (value, selectedYears = [], selectedMonths = [], selectedQuarters = []) => {
    const yearFilters = Array.isArray(selectedYears) ? selectedYears : []
    const monthFilters = Array.isArray(selectedMonths) ? selectedMonths : []
    const quarterFilters = Array.isArray(selectedQuarters) ? selectedQuarters : []
    if (yearFilters.length === 0 && monthFilters.length === 0 && quarterFilters.length === 0) return true
    const monthKey = toMonthKey(value)
    if (!monthKey) return false
    const [year, month] = monthKey.split('-')
    const yearShort = year.toString().slice(-2)
    const quarter = `Q${Math.ceil(Number(month) / 3)}'${yearShort}`

    if (yearFilters.length > 0 && !yearFilters.includes(year)) return false
    if (monthFilters.length > 0 && !monthFilters.includes(month)) return false
    if (quarterFilters.length > 0 && !quarterFilters.includes(quarter)) return false
    return true
  }

  const matchesProjectFilter = (recordProjectId, selectedProjectIds = []) => {
    const projectFilters = Array.isArray(selectedProjectIds) ? selectedProjectIds : []
    if (projectFilters.length === 0) return true
    return projectFilters.includes(String(recordProjectId || ''))
  }

  const getProjectCreditNoteTotal = (projectId) => {
    if (!projectId) return 0

    return creditNotes.reduce((sum, creditNote) => {
      const creditNoteProjectId = Number(creditNote?.Invoice?.project_id || 0)
      if (creditNoteProjectId !== Number(projectId)) {
        return sum
      }
      return sum + Number(creditNote?.amount_rm || 0)
    }, 0)
  }

  const calculateProjectRevenue = (project) => {
    // Revenue from paid Outgoing invoices associated with this project,
    // bucketed by payment_date (instead of project close status).
    const projectInvoices = invoices.filter((inv) => {
      const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
      const status = String(inv.status || '').trim()
      return Number(inv.project_id || 0) === Number(project.project_id) &&
             remarksType.toLowerCase() === 'outgoing' &&
             status.toLowerCase() === 'paid' &&
             inv.payment_date
    })
    return projectInvoices.reduce((sum, inv) => sum + Number(inv.invoice_amount || 0), 0)
  }

  const formatMoney = (value) => formatCurrency(value)

  // Records Summary helpers
  const getQuotationSummary = (rows) => {
    const claim = { Proposal: 0, Confirm: 0, Close: 0 }
    rows.forEach(q => {
      const status = q.status || 'Proposal'
      if (status in claim) claim[status]++
    })
    return Object.entries(claim)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({ name, value }))
  }

  const getPOSummary = (rows, invoiceRows) => {
    const claim = { Paid: 0, Unpaid: 0 }
    rows.forEach(po => {
      const poValue = Number(po.po_value || 0)
      const issuedInvoiceTotal = invoiceRows.reduce((sum, inv) => {
        const samePOById = inv.po_id && po.po_id && Number(inv.po_id) === Number(po.po_id)
        const samePOByNumber = inv?.PurchaseOrder?.po_number && po.po_number && inv.PurchaseOrder.po_number === po.po_number
        if (samePOById || samePOByNumber) {
          return sum + Number(inv.invoice_amount || inv.progress_amount || 0)
        }
        return sum
      }, 0)
      const status = poValue > 0 && issuedInvoiceTotal >= poValue ? 'Paid' : 'Unpaid'
      claim[status]++
    })
    return Object.entries(claim)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({ name, value }))
  }

  const getInvoiceSummary = (rows) => {
    const claim = { Paid: 0, Unpaid: 0 }
    rows.forEach(inv => {
      const status = inv.status === 'Paid' ? 'Paid' : 'Unpaid'
      claim[status]++
    })
    return Object.entries(claim)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({ name, value }))
  }

  const getExpenseSummary = (rows) => {
    const claim = { Pending: 0, Approve: 0, Reject: 0 }
    rows.forEach(e => {
      const raw = (e.status || 'Pending').toLowerCase()
      const status = raw === 'approved' || raw === 'approve'
        ? 'Approve'
        : raw === 'rejected' || raw === 'reject'
          ? 'Reject'
          : 'Pending'
      if (status in claim) claim[status]++
    })
    return Object.entries(claim)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({ name, value }))
  }

  const getPayrollSummary = (rows) => {
    const claim = { Unpaid: 0, Paid: 0 }
    rows.forEach(sr => {
      const status = sr.status === 'Paid' ? 'Paid' : 'Unpaid'
      claim[status]++
    })
    return Object.entries(claim)
      .filter(([, count]) => count > 0)
      .map(([name, value]) => ({ name, value }))
  }

  const getPeriodData = (periodType = 'month') => {
    const periodMap = new Map()

    // Revenue from paid Outgoing invoices bucketed by payment_date
    invoices
      .filter((inv) => {
        const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
        const status = String(inv.status || '').trim()
        return remarksType.toLowerCase() === 'outgoing' &&
               status.toLowerCase() === 'paid' &&
               inv.payment_date
      })
      .forEach((inv) => {
        const period = getPeriodKey(inv.payment_date, periodType)
        if (!period) return
        if (!periodMap.has(period)) {
          // Initialize with all expense categories
          const expenseCategories = Object.values(EXPENSE_CATEGORY_KEYS)
          const initialData = { period, income: 0 }
          expenseCategories.forEach(cat => {
            initialData[cat] = 0
          })
          periodMap.set(period, initialData)
        }
        periodMap.get(period).income += Number(inv.invoice_amount || 0)
      })

    expenses.forEach((e) => {
      const period = getPeriodKey(e.expense_date, periodType)
      if (!period) return
      if (!periodMap.has(period)) {
        // Initialize with all expense categories
        const expenseCategories = Object.values(EXPENSE_CATEGORY_KEYS)
        const initialData = { period, income: 0 }
        expenseCategories.forEach(cat => {
          initialData[cat] = 0
        })
        periodMap.set(period, initialData)
      }
      const categoryKey = EXPENSE_CATEGORY_KEYS[String(e.expense_category || '').trim().toLowerCase()]
      if (categoryKey && periodMap.get(period)[categoryKey] !== undefined) {
        periodMap.get(period)[categoryKey] += Number(e.amount || 0)
      }
    })

    // Add subcontractor costs from paid Incoming invoices (bills from subcontractors) ONLY
    invoices
      .filter((inv) => {
        const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
        const status = String(inv.status || '').trim()
        return remarksType.toLowerCase() === 'incoming' &&
               status.toLowerCase() === 'paid' &&
               inv.payment_date
      })
      .forEach((inv) => {
        const period = getPeriodKey(inv.payment_date, periodType)
        if (!period) return
        if (!periodMap.has(period)) {
          const expenseCategories = Object.values(EXPENSE_CATEGORY_KEYS)
          const initialData = { period, income: 0 }
          expenseCategories.forEach(cat => {
            initialData[cat] = 0
          })
          periodMap.set(period, initialData)
        }
        periodMap.get(period).subcon += Number(inv.invoice_amount || 0)
      })

    // Add salary/payroll data to overhead category by period
    salaryRecords.forEach((sr) => {
      const period = getPeriodKey(sr.period || sr.payment_date || sr.updated_at || sr.created_at, periodType)
      if (!period) return
      if (!periodMap.has(period)) {
        const expenseCategories = Object.values(EXPENSE_CATEGORY_KEYS)
        const initialData = { period, income: 0 }
        expenseCategories.forEach(cat => {
          initialData[cat] = 0
        })
        periodMap.set(period, initialData)
      }
      periodMap.get(period).overhead += Number(sr.amount || sr.total_amount || 0)
    })

    // Sort newest period first. Labels are display strings ("May'26", "Q2'25",
    // "2026"), so parse them back into comparable (year, index) keys instead of
    // a plain string sort, which would misorder cross-year months.
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const periodSortKey = (label) => {
      if (!label) return [0, 0]
      const annualMatch = String(label).match(/^(\d{4})$/)
      if (annualMatch) return [Number(annualMatch[1]), 0]
      const quarterMatch = String(label).match(/^Q(\d)'(\d{2})$/)
      if (quarterMatch) return [2000 + Number(quarterMatch[2]), Number(quarterMatch[1])]
      const monthMatch = String(label).match(/^([A-Za-z]{3})'(\d{2})$/)
      if (monthMatch) return [2000 + Number(monthMatch[2]), monthNames.indexOf(monthMatch[1]) + 1]
      return [0, 0]
    }

    return Array.from(periodMap.values())
      .sort((a, b) => {
        const [aYear, aIndex] = periodSortKey(a.period)
        const [bYear, bIndex] = periodSortKey(b.period)
        return bYear - aYear || bIndex - aIndex
      })
      .map((row) => ({
        ...row,
        overhead: Number(row.overhead.toFixed(2)),
        rental: Number(row.rental.toFixed(2)),
        material: Number(row.material.toFixed(2)),
        subcon: Number(row.subcon.toFixed(2)),
        petrol: Number(row.petrol.toFixed(2)),
        others: Number(row.others.toFixed(2)),
        income: Number(row.income.toFixed(2))
      }))
  }

  const resolveProjectLabel = (project, labelType) => {
    if (labelType === 'po_no') {
      return project.po_no || project.project_code || project.project_name || `P-${project.project_id}`
    }
    return project.project_name || project.po_no || project.project_code || `P-${project.project_id}`
  }

  const getProjectData = (selectedYears, selectedMonths, selectedProjectIds, labelType = 'project_name') => {
    const projectMap = new Map()

    projects.forEach((p) => {
      if (!matchesProjectFilter(p.project_id, selectedProjectIds)) return
      projectMap.set(p.project_id, {
        name: resolveProjectLabel(p, labelType),
        projectId: p.project_id,
        income: 0,
        overhead: 0,
        rental: 0,
        material: 0,
        subcon: 0,
        petrol: 0,
        others: 0
      })
    })

    // Revenue from paid Outgoing invoices, bucketed by payment_date, grouped by project
    invoices
      .filter((inv) => {
        const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
        const status = String(inv.status || '').trim()
        return remarksType.toLowerCase() === 'outgoing' &&
               status.toLowerCase() === 'paid' &&
               inv.payment_date
      })
      .forEach((inv) => {
        if (!matchesProjectFilter(inv.project_id, selectedProjectIds)) return
        if (!matchesDateFilter(inv.payment_date, selectedYears, selectedMonths)) return
        if (!projectMap.has(inv.project_id)) {
          projectMap.set(inv.project_id, {
            name: resolveProjectLabel({ project_id: inv.project_id, project_name: inv.Project?.project_name }, labelType),
            projectId: inv.project_id,
            income: 0,
            overhead: 0,
            rental: 0,
            material: 0,
            subcon: 0,
            petrol: 0,
            others: 0
          })
        }
        projectMap.get(inv.project_id).income += Number(inv.invoice_amount || 0)
      })

    expenses.forEach((e) => {
      if (!matchesProjectFilter(e.project_id, selectedProjectIds)) return
      if (!matchesDateFilter(e.expense_date, selectedYears, selectedMonths)) return
      if (!projectMap.has(e.project_id)) return
      const categoryKey = EXPENSE_CATEGORY_KEYS[String(e.expense_category || '').trim().toLowerCase()]
      if (categoryKey) projectMap.get(e.project_id)[categoryKey] += Number(e.amount || 0)
    })

    // Add subcontractor costs from paid Incoming invoices (bills from subcontractors) ONLY
    invoices
      .filter((inv) => {
        const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
        const status = String(inv.status || '').trim()
        return remarksType.toLowerCase() === 'incoming' &&
               status.toLowerCase() === 'paid' &&
               inv.payment_date
      })
      .forEach((inv) => {
        if (!matchesProjectFilter(inv.project_id, selectedProjectIds)) return
        if (!matchesDateFilter(inv.payment_date, selectedYears, selectedMonths)) return
        if (!projectMap.has(inv.project_id)) {
          projectMap.set(inv.project_id, {
            name: resolveProjectLabel({ project_id: inv.project_id, project_name: inv.Project?.project_name }, labelType),
            projectId: inv.project_id,
            income: 0,
            overhead: 0,
            rental: 0,
            material: 0,
            subcon: 0,
            petrol: 0,
            others: 0
          })
        }
        projectMap.get(inv.project_id).subcon += Number(inv.invoice_amount || 0)
      })

    attendances.forEach((a) => {
      if (!matchesProjectFilter(a.project_id, selectedProjectIds)) return
      if (!matchesDateFilter(a.submission_date || a.attendance_date || a.created_at, selectedYears, selectedMonths)) return
      if (!projectMap.has(a.project_id)) return
      // Add salary amount to overhead category (salary = overhead in operations)
      projectMap.get(a.project_id).overhead += Number(a.amount || 0)
      projectMap.get(a.project_id).salary += Number(a.amount || 0)
    })

    return Array.from(projectMap.values())
      .map((row) => ({
        ...row,
        income: Number((row.income || 0).toFixed(2)),
        overhead: Number((row.overhead || 0).toFixed(2)),
        rental: Number((row.rental || 0).toFixed(2)),
        material: Number((row.material || 0).toFixed(2)),
        subcon: Number((row.subcon || 0).toFixed(2)),
        petrol: Number((row.petrol || 0).toFixed(2)),
        others: Number((row.others || 0).toFixed(2)),
        salary: Number((row.salary || 0).toFixed(2))
      }))
      .filter((r) => r.income > 0 || r.overhead > 0 || r.rental > 0 || r.material > 0 || r.subcon > 0 || r.petrol > 0 || r.others > 0 || r.salary > 0)
  }

  const allDateValues = [
    ...projects.map((p) => p.close_date || p.updated_at || p.created_at),
    ...expenses.map((e) => e.expense_date),
    ...attendances.map((a) => a.submission_date || a.attendance_date || a.created_at),
    ...quotations.map((q) => q.quotation_date || q.updated_at || q.created_at),
    ...purchaseOrders.map((po) => po.po_date || po.updated_at || po.created_at),
    ...invoices.map((inv) => inv.invoice_date || inv.updated_at || inv.created_at),
    ...salaryRecords.map((sr) => sr.period || sr.payment_date || sr.updated_at || sr.created_at)
  ]

  const yearOptions = Array.from(
    new Set(
      allDateValues
        .map((value) => toYear(value))
        .filter(Boolean)
    )
  ).sort((a, b) => Number(b) - Number(a))

  // Generate quarter options based on available years
  const quarterOptions = yearOptions.flatMap(year => {
    const yearShort = year.toString().slice(-2)
    return [
      { value: `Q1'${yearShort}`, label: `Q1'${yearShort}` },
      { value: `Q2'${yearShort}`, label: `Q2'${yearShort}` },
      { value: `Q3'${yearShort}`, label: `Q3'${yearShort}` },
      { value: `Q4'${yearShort}`, label: `Q4'${yearShort}` }
    ]
  })

  // Get active filters early since it's used throughout the component
  const activeFilters = filters[activeView]

  const periodData = getPeriodData(filters.operations.periodType || 'month')
  const periodFilters = filters.operations
  const filteredPeriodData = periodData.filter((row) => {
    // Parse the period format based on type
    let year, month, quarter

    if (periodFilters.periodType === 'annual') {
      // Format: YYYY
      year = row.period
    } else if (periodFilters.periodType === 'quarter') {
      // Format: Q#'YY
      const match = row.period.match(/Q(\d)'(\d{2})/)
      if (match) {
        quarter = match[0]
        year = '20' + match[2] // Convert 'YY to 20YY
      }
    } else {
      // Format: MMM'YY
      const match = row.period.match(/(\w{3})'(\d{2})/)
      if (match) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        month = String(monthNames.indexOf(match[1]) + 1).padStart(2, '0')
        year = '20' + match[2] // Convert 'YY to 20YY
      }
    }

    if (!year) return false

    // Apply year filter (applies to all view types)
    if (periodFilters.years.length > 0 && !periodFilters.years.includes(year)) return false

    // Apply month filter (only for monthly view)
    if (periodFilters.periodType === 'month' && periodFilters.months.length > 0 && month && !periodFilters.months.includes(month)) return false

    // Apply quarter filter (only for quarterly view)
    if (periodFilters.periodType === 'quarter' && periodFilters.quarters && periodFilters.quarters.length > 0 && quarter && !periodFilters.quarters.includes(quarter)) return false

    return true
  })

  const projectFilters = filters.operations
  const filteredProjectData = getProjectData(projectFilters.years, projectFilters.months, projectFilters.projectIds, projectFilters.labelType)

  const claimFilters = filters.operations
  const filteredSummaryQuotations = quotations.filter((q) => (
    matchesDateFilter(q.quotation_date || q.updated_at || q.created_at, claimFilters.years, claimFilters.months) &&
    matchesProjectFilter(toProjectId(q), claimFilters.projectIds)
  ))
  const filteredSummaryPO = purchaseOrders.filter((po) => (
    matchesDateFilter(po.po_date || po.updated_at || po.created_at, claimFilters.years, claimFilters.months) &&
    matchesProjectFilter(toProjectId(po), claimFilters.projectIds)
  ))
  const filteredSummaryInvoices = invoices.filter((inv) => {
    const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
    const status = String(inv.status || '').trim()
    return remarksType.toLowerCase() === 'outgoing' &&
           status.toLowerCase() === 'paid' &&
           inv.payment_date &&
           matchesDateFilter(inv.payment_date, claimFilters.years, claimFilters.months) &&
           matchesProjectFilter(toProjectId(inv), claimFilters.projectIds)
  })
  const filteredSummaryExpenses = expenses.filter((e) => (
    matchesDateFilter(e.expense_date, claimFilters.years, claimFilters.months) &&
    matchesProjectFilter(toProjectId(e), claimFilters.projectIds)
  ))
  const filteredSummaryPayroll = salaryRecords.filter((sr) => (
    matchesDateFilter(sr.period || sr.payment_date || sr.updated_at || sr.created_at, claimFilters.years, claimFilters.months) &&
    matchesProjectFilter(toProjectId(sr), claimFilters.projectIds)
  ))

  const filteredSummaryIncome = filteredSummaryInvoices
    .reduce((sum, inv) => sum + Number(inv.invoice_amount || 0), 0)

  const filteredSummaryExpenseBreakdown = filteredSummaryExpenses.reduce(
    (acc, row) => {
      const categoryKey = EXPENSE_CATEGORY_KEYS[String(row.expense_category || '').trim().toLowerCase()]
      if (categoryKey) acc[categoryKey] += Number(row.amount || 0)
      return acc
    },
    { overhead: 0, rental: 0, material: 0, subcon: 0, petrol: 0, others: 0 }
  )

  // Add subcontractor costs from paid Incoming invoices (bills from subcontractors) ONLY
  const filteredSummaryIncomingInvoices = invoices.filter((inv) => {
    const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
    const status = String(inv.status || '').trim()
    return remarksType.toLowerCase() === 'incoming' &&
           status.toLowerCase() === 'paid' &&
           inv.payment_date &&
           matchesDateFilter(inv.payment_date, claimFilters.years, claimFilters.months) &&
           matchesProjectFilter(toProjectId(inv), claimFilters.projectIds)
  })
  filteredSummaryIncomingInvoices.forEach((inv) => {
    filteredSummaryExpenseBreakdown.subcon += Number(inv.invoice_amount || 0)
  })

  // Add salary/payroll data to overhead category using existing filteredSummaryPayroll
  filteredSummaryPayroll.forEach((sr) => {
    filteredSummaryExpenseBreakdown.overhead += Number(sr.amount || sr.total_amount || 0)
  })


  const claimFinancialData = [{
    income: Number(filteredSummaryIncome.toFixed(2)),
    overhead: Number(filteredSummaryExpenseBreakdown.overhead.toFixed(2)),
    rental: Number(filteredSummaryExpenseBreakdown.rental.toFixed(2)),
    material: Number(filteredSummaryExpenseBreakdown.material.toFixed(2)),
    subcon: Number(filteredSummaryExpenseBreakdown.subcon.toFixed(2)),
    petrol: Number(filteredSummaryExpenseBreakdown.petrol.toFixed(2)),
    others: Number(filteredSummaryExpenseBreakdown.others.toFixed(2))
  }]

  const selectedData = activeView === 'operations' && activeFilters.viewMode === 'period'
    ? filteredPeriodData
    : activeView === 'operations' && activeFilters.viewMode === 'project'
      ? filteredProjectData
      : claimFinancialData

  const xAxisCategories = selectedData.map((row) => (activeView === 'operations' && activeFilters.viewMode === 'period' ? row.period : row.name))
  const incomeVsExpensesData = selectedData.map((row) => ({
    ...row,
    expenses: Number((
      Number(row.overhead || 0) +
      Number(row.rental || 0) + Number(row.material || 0) +
      Number(row.subcon || 0) + Number(row.petrol || 0) +
      Number(row.others || 0)
    ).toFixed(2))
  }))

  const quotationSummary = getQuotationSummary(filteredSummaryQuotations)
  const poSummary = getPOSummary(filteredSummaryPO, filteredSummaryInvoices)
  const invoiceSummary = getInvoiceSummary(filteredSummaryInvoices)
  const expenseSummary = getExpenseSummary(filteredSummaryExpenses)
  const payrollSummary = getPayrollSummary(filteredSummaryPayroll)

  const resetActiveFilters = () => {
    setFilters((prev) => ({
      ...prev,
      [activeView]: activeView === 'operations' && activeFilters.viewMode === 'period'
        ? { years: [], months: [], periodType: 'month' }
        : activeView === 'operations' && activeFilters.viewMode === 'project'
          ? { ...prev.operations, years: [], months: [], projectIds: [] }
          : { years: [], months: [], projectIds: [] }
    }))
  }

  const projectOptions = projects.map((project) => ({
    value: String(project.project_id),
    label: `${project.po_no || project.project_code || 'N/A'} - ${project.project_name}`
  }))

  // Expenses first so it renders at the BOTTOM of the stacked bar — the red
  // segment fills up from the baseline, and the green Revenue segment above it
  // lets viewers compare how much of the revenue the expenses consume.
  const incomeVsExpensesSeries = [
    {
      name: 'Expenses',
      data: incomeVsExpensesData.map((row) => Number(row.expenses || 0))
    },
    {
      name: 'Revenue',
      data: incomeVsExpensesData.map((row) => Number(row.income || 0))
    }
  ]

  // Dynamically generate expense breakdown series based on available categories
  // Force show the 5 main categories as requested: Overhead, Rental, Petrol, Subcon, Others
  const MAIN_EXPENSE_CATEGORIES = ['overhead', 'rental', 'petrol', 'subcon', 'others']
  const expenseCategories = Object.values(EXPENSE_CATEGORY_KEYS)

  const expensesBreakdownSeries = MAIN_EXPENSE_CATEGORIES
    .map(category => ({
      name: category.charAt(0).toUpperCase() + category.slice(1), // Capitalize first letter
      data: selectedData.map((row) => Number(row[category] || 0))
    }))
    .filter(series => {
      // Always include all 5 main categories even if data is temporarily missing
      return MAIN_EXPENSE_CATEGORIES.includes(series.name.toLowerCase())
    })

  const profitLossData = incomeVsExpensesData.map(
    (row) => Number((Number(row.income || 0) - Number(row.expenses || 0)).toFixed(2))
  )

  // Top KPI cards should always show overall values from full datasets.
  const overallRevenue = Number(
    invoices
      .filter((inv) => {
        const remarksType = String(inv.remarks_type || inv.remarksType || '').trim()
        const status = String(inv.status || '').trim()
        return remarksType.toLowerCase() === 'outgoing' &&
               status.toLowerCase() === 'paid' &&
               inv.payment_date
      })
      .reduce((sum, inv) => sum + Number(inv.invoice_amount || 0), 0)
      .toFixed(2)
  )
  const overallExpenses = Number(
    expenses.reduce((sum, row) => sum + Number(row.amount || 0), 0).toFixed(2)
  )
  const overallSalary = Number(
    salaryRecords.reduce((sum, row) => sum + Number(row.salary_amount || row.amount || row.total_amount || 0), 0).toFixed(2)
  )

  // Calculate average profit margin: ((Revenue - Expenses) / Revenue) * 100
  const averageProfitMargin = overallRevenue > 0
    ? Number(((overallRevenue - overallExpenses - overallSalary) / overallRevenue * 100).toFixed(1))
    : 0

  const overallNet = Number((overallRevenue - overallExpenses - overallSalary).toFixed(2))

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-4xl mb-4">⏳</div>
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      <h1 className={`text-3xl font-bold ${isDark ? 'text-slate-100' : 'text-gray-800'}`}>Dashboard</h1>

      <div className={`rounded-lg p-4 shadow md:hidden ${isDark ? 'bg-slate-900 ring-1 ring-slate-700' : 'bg-white'}`}>
        <label htmlFor="dashboard-view" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
          Monitoring View
        </label>
        <select
          id="dashboard-view"
          value={activeView}
          onChange={(e) => setActiveView(e.target.value)}
          className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100' : 'border-gray-300 text-gray-700'}`}
        >
          <option value="operations">Operation Dashboard</option>
          <option value="financial">Financial Dashboard</option>
        </select>
      </div>

      <div className="mt-6"></div>

      <div className={`hidden gap-0 rounded-t-lg border-b md:flex ${isDark ? 'border-slate-700 bg-slate-900' : 'border-gray-300 bg-white'}`}>
        <button
          onClick={() => setActiveView('operations')}
          className={`px-6 py-3 font-medium transition ${
            activeView === 'operations'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : isDark
                ? 'text-slate-300 hover:text-slate-100'
                : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Operation Dashboard
        </button>
        <button
          onClick={() => setActiveView('financial')}
          className={`px-6 py-3 font-medium transition ${
            activeView === 'financial'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : isDark
                ? 'text-slate-300 hover:text-slate-100'
                : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Financial Dashboard
        </button>
      </div>

      <div className="mt-6"></div>

      {activeView !== 'financial' && (
        <CollapsibleFilters
          title='Operation Dashboard Filters'
          className={`rounded-lg shadow ${isDark ? 'bg-slate-900 ring-1 ring-slate-700' : 'bg-white'}`}
          contentClassName={`p-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}
        >
          <div className={`grid gap-3 md:grid-cols-4`}>
            {/* View Mode Selector */}
            <div>
              <select
                value={activeFilters.viewMode || 'period'}
                onChange={(event) => {
                  const viewMode = event.target.value
                  setFilters((prev) => ({
                    ...prev,
                    operations: {
                      ...prev.operations,
                      viewMode,
                      // Reset filters when changing view modes
                      months: [],
                      projectIds: viewMode === 'project' || viewMode === 'claim' ? [] : []
                    }
                  }))
                }}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="period">📅 Period View</option>
                <option value="project">🏗️ Project View</option>
                <option value="claim">📊 Claim View</option>
              </select>
            </div>
          <MultiSelectFilterDropdown
            options={yearOptions}
            selectedValues={activeFilters.years}
            onChange={(years) => {
              setFilters((prev) => ({
                ...prev,
                [activeView]: {
                  ...prev[activeView],
                  years
                }
              }))
            }}
            placeholder="Years"
            emptyText="No years"
            className={isDark ? 'text-gray-900' : ''}
          />
          {activeView === 'operations' && activeFilters.viewMode === 'period' && activeFilters.periodType === 'month' && (
            <div>
            <MultiSelectFilterDropdown
              options={MONTH_OPTIONS.map((month) => ({ value: month.value, label: month.label }))}
              selectedValues={activeFilters.months}
              onChange={(months) => {
                const sortedMonths = months.sort((a, b) => b.localeCompare(a)) // Sort descending
                setFilters((prev) => ({
                  ...prev,
                  operations: {
                    ...prev.operations,
                    months: sortedMonths
                  }
                }))
              }}
              placeholder="Months"
              emptyText="No months"
              className={isDark ? 'text-gray-900' : ''}
            />
            </div>
          )}

          {activeView === 'operations' && activeFilters.viewMode === 'period' && activeFilters.periodType === 'quarter' && (
            <div>
            <MultiSelectFilterDropdown
              options={quarterOptions}
              selectedValues={activeFilters.quarters || []}
              onChange={(quarters) => {
                const sortedQuarters = quarters.sort((a, b) => b.localeCompare(a)) // Sort descending
                setFilters((prev) => ({
                  ...prev,
                  operations: {
                    ...prev.operations,
                    quarters: sortedQuarters
                  }
                }))
              }}
              placeholder="Quarters"
              emptyText="No quarters"
              className={isDark ? 'text-gray-900' : ''}
            />
            </div>
          )}

          {activeView === 'operations' && activeFilters.viewMode === 'period' && (
            <div>
              <select
                value={activeFilters.periodType || 'month'}
                onChange={(event) => {
                  const periodType = event.target.value
                  setFilters((prev) => ({
                    ...prev,
                    operations: {
                      ...prev.operations,
                      periodType,
                      months: periodType === 'month' ? prev.operations.months : [],
                      quarters: periodType === 'quarter' ? prev.operations.quarters || [] : []
                    }
                  }))
                }}
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PERIOD_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}

          {activeView !== 'period' && (
            <>
              <div>
                <MultiSelectFilterDropdown
                  options={projectOptions}
                  selectedValues={activeFilters.projectIds}
                  onChange={(projectIds) => {
                    setFilters((prev) => ({
                      ...prev,
                      [activeView]: {
                        ...prev[activeView],
                        projectIds
                      }
                    }))
                  }}
                  placeholder="Projects"
                  emptyText="No projects"
                  className={isDark ? 'text-gray-900' : ''}
                />
              </div>
              {activeView === 'operations' && activeFilters.viewMode === 'project' && (
                <div>
                  <select
                    value={activeFilters.labelType || 'project_name'}
                    onChange={(event) => {
                      const labelType = event.target.value
                      setFilters((prev) => ({
                        ...prev,
                        project: {
                          ...prev.operations,
                          labelType
                        }
                      }))
                    }}
                    className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="project_name">Visualize by Project Name</option>
                    <option value="po_no">Visualize by PO No.</option>
                  </select>
                </div>
              )}
            </>
          )}

          <button
            type="button"
            onClick={resetActiveFilters}
            className="btn-reset-standard"
          >
            🔄 Reset Filters
          </button>
        </div>
      </CollapsibleFilters>
      )}

      <div className="mt-6"></div>

      {activeView === 'operations' && activeFilters.viewMode !== 'claim' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FlowbiteBarChart
            title={`Revenue vs Expenses by ${activeFilters.periodType === 'quarter' ? 'Quarter' : activeFilters.periodType === 'annual' ? 'Year' : 'Month'}`}
            categories={xAxisCategories}
            series={incomeVsExpensesSeries}
            stacked={false}
            isDark={isDark}
          />

          <FlowbiteBarChart
            title={`Expenses Breakdown by ${activeFilters.periodType === 'quarter' ? 'Quarter' : activeFilters.periodType === 'annual' ? 'Year' : 'Month'}`}
            categories={xAxisCategories}
            series={expensesBreakdownSeries}
            isDark={isDark}
          />

          <div className="lg:col-span-2">
            <ProfitLossBarChart
              title={`Profit / Loss by ${activeFilters.periodType === 'quarter' ? 'Quarter' : activeFilters.periodType === 'annual' ? 'Year' : 'Month'}`}
              categories={xAxisCategories}
              data={profitLossData}
              isDark={isDark}
            />
          </div>
        </div>
      )}

      {activeView === 'operations' && activeFilters.viewMode === 'claim' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FlowbitePieChart title="Quotations" data={quotationSummary} isDark={isDark} />
          <FlowbitePieChart title="Purchase Orders" data={poSummary} isDark={isDark} />
          <FlowbitePieChart title="Invoices" data={invoiceSummary} isDark={isDark} />
          <FlowbitePieChart title="Expenses" data={expenseSummary} isDark={isDark} />
          <FlowbitePieChart title="Payroll" data={payrollSummary} isDark={isDark} />
        </div>
      )}

      {activeView === 'financial' && (
        <FinancialStatements />
      )}
    </div>
  )
}
